import {
  DRAFT_KEY_PREFIX,
  LOCAL_DRAFT_MAX_AGE_MS,
  NOTICE_KEY,
} from './constants'
import type { LocalDraft } from './types'

/**
 * `localStorage` can throw (private window, blocked site data) or come back empty; every
 * helper degrades to "nothing stored" and the page works without it.
 */
function read(key: string): string | null {
  try {
    return localStorage.getItem(key)
  } catch {
    return null
  }
}

function write(key: string, value: string): boolean {
  try {
    localStorage.setItem(key, value)
    return true
  } catch {
    return false
  }
}

function remove(key: string): boolean {
  try {
    localStorage.removeItem(key)
    return true
  } catch {
    return false
  }
}

function keys(prefix: string): string[] {
  try {
    const found: string[] = []
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i)
      if (key?.startsWith(prefix)) found.push(key)
    }
    return found
  } catch {
    return []
  }
}

function parseDraft(raw: string | null): LocalDraft | null {
  if (!raw) return null
  try {
    const value = JSON.parse(raw) as Partial<LocalDraft> | null
    if (!value || typeof value.text !== 'string' || typeof value.savedAt !== 'number') return null
    return {
      text: value.text,
      savedAt: value.savedAt,
      context: value.context ?? null,
      ...(value.inputMethod ? { inputMethod: value.inputMethod } : {}),
    }
  } catch {
    return null
  }
}

export function saveLocalDraft(clientCaptureId: string, draft: LocalDraft): boolean {
  return write(DRAFT_KEY_PREFIX + clientCaptureId, JSON.stringify(draft))
}

export function loadLocalDraft(clientCaptureId: string): LocalDraft | null {
  return parseDraft(read(DRAFT_KEY_PREFIX + clientCaptureId))
}

export function removeLocalDraft(clientCaptureId: string): void {
  remove(DRAFT_KEY_PREFIX + clientCaptureId)
}

/** Deletes copies older than seven days (and unreadable ones). */
export function pruneLocalDrafts(now: number = Date.now()): void {
  for (const key of keys(DRAFT_KEY_PREFIX)) {
    const draft = parseDraft(read(key))
    if (!draft || now - draft.savedAt > LOCAL_DRAFT_MAX_AGE_MS) remove(key)
  }
}

/** The newest local copy, with the client id it belongs to. */
export function newestLocalDraft(): { clientCaptureId: string; draft: LocalDraft } | null {
  let best: { clientCaptureId: string; draft: LocalDraft } | null = null
  for (const key of keys(DRAFT_KEY_PREFIX)) {
    const draft = parseDraft(read(key))
    if (draft && (!best || draft.savedAt > best.draft.savedAt)) {
      best = { clientCaptureId: key.slice(DRAFT_KEY_PREFIX.length), draft }
    }
  }
  return best
}

/** The notice version this device has seen saved on the server, or 0. */
export function deviceNoticeVersion(): number {
  const value = Number(read(NOTICE_KEY))
  return Number.isFinite(value) ? value : 0
}

export function rememberNoticeOnDevice(version: number): void {
  if (version > deviceNoticeVersion()) write(NOTICE_KEY, String(version))
}

const LANG_KEY = 'vonk.inspreken.lang'

/** The dictation language this device chose ("nl-NL" unless "en-US" was picked). */
export function deviceDictationLang(): 'nl-NL' | 'en-US' {
  return read(LANG_KEY) === 'en-US' ? 'en-US' : 'nl-NL'
}

export function rememberDictationLang(lang: 'nl-NL' | 'en-US'): void {
  write(LANG_KEY, lang)
}
